import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Mail, Phone, Globe } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img src="/isotipo.png" alt="DonaApp" className="h-9 w-9" />
              <img src="/logotipo.png" alt="DonaApp" className="h-6 hidden sm:block" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-indigo-600 text-sm font-medium transition-colors">
                Inicio
              </Link>
              <Link to="/marketplace" className="text-gray-600 hover:text-indigo-600 text-sm font-medium transition-colors">
                Marketplace
              </Link>
              {isLoggedIn && (
                <Link to="/dashboard" className="text-gray-600 hover:text-indigo-600 text-sm font-medium transition-colors">
                  Dashboard
                </Link>
              )}
              {isLoggedIn && (
                <Link to="/profile" className="text-gray-600 hover:text-indigo-600 text-sm font-medium transition-colors">
                  Perfil
                </Link>
              )}
            </nav>

            {/* Desktop Auth Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              {isLoggedIn ? (
                <Link to="/dashboard" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
                  Mi Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">
                    Iniciar Sesión
                  </Link>
                  <Link to="/register" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors">
                    Registrarse
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-indigo-600"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-indigo-600 font-medium">
                Inicio
              </Link>
              <Link to="/marketplace" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-indigo-600 font-medium">
                Marketplace
              </Link>
              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-indigo-600 font-medium">
                    Dashboard
                  </Link>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="block text-gray-700 hover:text-indigo-600 font-medium">
                    Mi Perfil
                  </Link>
                </>
              ) : (
                <div className="pt-3 border-t space-y-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg">
                    Iniciar Sesión
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg">
                    Registrarse
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      
      <main className="flex-1">
        {children}
      </main>
      
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Columna Marca */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <img src="/isotipo.png" alt="DonaApp" className="h-8 w-8" />
                <span className="text-white font-semibold text-lg">DonaApp</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Plataforma que conecta donantes con causas verificadas mediante donaciones en especie.
              </p>
            </div>

            {/* Columna Legal */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Información Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="font-medium text-gray-300">OVIEDOTECH S.A.S.</li>
                <li>NIT: 901.964.524-2</li>
                <li>Cra. 46 #45-88, Cali, Valle del Cauca, Colombia</li>
              </ul>
            </div>

            {/* Columna Contacto */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Contacto</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <a href="mailto:contacto@donaapp.co" className="hover:text-indigo-400 transition-colors">contacto@donaapp.co</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <a href="tel:+573169277199" className="hover:text-indigo-400 transition-colors">+57 316 927 7199</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 flex-shrink-0" />
                  <a href="https://donaapp.co" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400 transition-colors">donaapp.co</a>
                </li>
              </ul>
            </div>

            {/* Columna Enlaces */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Enlaces</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/marketplace" className="text-gray-400 hover:text-indigo-400 transition-colors">Marketplace</Link></li>
                <li><Link to="/acerca-de" className="text-gray-400 hover:text-indigo-400 transition-colors">Acerca de</Link></li>
                <li><Link to="/faq" className="text-gray-400 hover:text-indigo-400 transition-colors">Preguntas Frecuentes</Link></li>
                <li><Link to="/terminos-y-condiciones" className="text-gray-400 hover:text-indigo-400 transition-colors">Términos y Condiciones</Link></li>
                <li><Link to="/politica-de-privacidad" className="text-gray-400 hover:text-indigo-400 transition-colors">Política de Privacidad</Link></li>
              </ul>
            </div>
          </div>

          {/* Línea inferior */}
          <div className="border-t border-gray-800 mt-10 pt-6 text-center text-xs text-gray-500">
            © {new Date().getFullYear()} DonaApp — OVIEDOTECH S.A.S. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
